import {
  ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
  EstablishedAhpSessionResumptionTimeoutError,
  type EstablishedAhpResumptionAttempt,
  type EstablishedAhpResumptionPhase,
} from './contracts'

function abortReason(signal: AbortSignal): Error {
  return signal.reason instanceof Error
    ? signal.reason
    : new EstablishedAhpSessionResumptionTimeoutError(ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS)
}

export function createEstablishedAhpResumptionDeadline(
  timeoutMs = ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
): EstablishedAhpResumptionAttempt {
  const controller = new AbortController()
  let phase: EstablishedAhpResumptionPhase = 'initial'
  let expiresAt = Date.now() + timeoutMs
  let timer: ReturnType<typeof setTimeout> | undefined
  let disposed = false
  const remainingMs = () => Math.max(0, expiresAt - Date.now())
  const arm = () => {
    if (disposed || controller.signal.aborted) return
    const remaining = remainingMs()
    if (remaining === 0) {
      if (timer) clearTimeout(timer)
      timer = undefined
      controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
      return
    }
    if (timer) return
    timer = setTimeout(() => controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs)), remaining)
  }
  const rearm = () => {
    if (!timer) return
    clearTimeout(timer)
    timer = undefined
    arm()
  }
  const phaseRank: Record<EstablishedAhpResumptionPhase, number> = {
    initial: 0,
    waking: 1,
    connecting: 2,
  }

  return {
    get signal() {
      arm()
      return controller.signal
    },
    get phase() {
      return phase
    },
    timeoutMs,
    get expiresAt() {
      return expiresAt
    },
    get isDisposed() {
      return disposed
    },
    remainingMs,
    advanceTo: nextPhase => {
      arm()
      if (controller.signal.aborted) throw abortReason(controller.signal)
      if (phaseRank[nextPhase] <= phaseRank[phase]) return
      phase = nextPhase
      expiresAt = Date.now() + timeoutMs
      rearm()
    },
    waitFor: async work => {
      arm()
      if (controller.signal.aborted) return Promise.reject(abortReason(controller.signal))

      let onAbort = () => {}
      const aborted = new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(abortReason(controller.signal))
        controller.signal.addEventListener('abort', onAbort, {once: true})
      })
      try {
        const result = await Promise.race([work, aborted])
        if (remainingMs() === 0) {
          if (!controller.signal.aborted) {
            controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
          }
          throw abortReason(controller.signal)
        }
        return result
      } finally {
        controller.signal.removeEventListener('abort', onAbort)
      }
    },
    cancel: reason => {
      if (timer) clearTimeout(timer)
      timer = undefined
      controller.abort(reason ?? new DOMException('The resumption attempt was cancelled.', 'AbortError'))
    },
    dispose: () => {
      disposed = true
      if (timer) clearTimeout(timer)
      timer = undefined
    },
  }
}
